import { useEffect, useMemo, useState } from 'react'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Button, EmptyState, Modal, NativeSelect, Pagination, Spinner, StatusPill } from '../../../ui'
import { CategoryMeta } from '../commsInboxView/ReplyDetailPane'
import { formatDateTime } from '../commsInboxView/shared'
import { ConversationTrail } from '../commsMailboxWorkspaceView/ConversationTrail'
import { MailboxRef } from '../commsShared/mailboxes'
import { EVIDENCE_LABEL, EvidenceState, FLAG_LABEL, DeepDiveScope, Subcategory, ThreadLabel, deepDiveApi, messageOf } from '../commsShared/deepDive'
import styles from './styles.module.scss'

interface Props {
    scope: DeepDiveScope
    subcategories: Subcategory[]
    /** null = every labelled thread at this outcome. */
    subcategoryId: string | null
    evidenceState: EvidenceState | null
    flaggedOnly: boolean
    /** Bumped by the parent when labels change elsewhere. */
    version: number
    onChanged: () => void
    /** Labels for category keys shown inside the thread modal. */
    categoryMeta?: Map<string, CategoryMeta>
    mailboxes?: MailboxRef[]
}

const PAGE = 25

/** The mailbox page opened on exactly this thread. */
const threadHref = (t: ThreadLabel) => {
    const params = new URLSearchParams({ tab: 'received', view: 'threads', bucket: 'all', conversation: t.conversationId })
    return `/comms/mailboxes/${encodeURIComponent(t.thread?.mailbox ?? 'all')}?${params.toString()}`
}

export const EvidencePill = ({ state, size = 'sm' }: { state: EvidenceState; size?: 'sm' | 'md' }) => {
    const meta = EVIDENCE_LABEL[state] ?? { text: state, hint: '' }
    return (
        <StatusPill size={size} tone={state === 'verified' ? 'good' : state === 'needs_check' ? 'warn' : 'accent'} title={meta.hint}>
            {meta.text}
        </StatusPill>
    )
}

export const FlagPills = ({ flags, size = 'sm' }: { flags: string[]; size?: 'sm' | 'md' }) => (
    <>
        {flags.map((f) => {
            const meta = FLAG_LABEL[f] ?? { text: f.replace(/_/g, ' '), hint: '' }
            return (
                <StatusPill key={f} size={size} tone={f === 'injection_suspect' ? 'bad' : 'warn'} title={meta.hint}>
                    {meta.text}
                </StatusPill>
            )
        })}
    </>
)

/**
 * Every labelled thread in one sub-category (or every one at the outcome):
 * who wrote, the details the model pulled out, and the quoted wording from the
 * email that supports the label, so a label can be checked without opening the
 * email. "Move to…" puts a thread under a different sub-category.
 */
export const SubcategoryThreads = ({ scope, subcategories, subcategoryId, evidenceState, flaggedOnly, version, onChanged, categoryMeta, mailboxes }: Props) => {
    const navigate = useNavigate()
    const [page, setPage] = useState(1)
    const [rows, setRows] = useState<ThreadLabel[] | null>(null)
    const [total, setTotal] = useState(0)
    const [error, setError] = useState<string | null>(null)
    const [moving, setMoving] = useState<string | null>(null)
    /** Thread open in the modal; the list and its filters stay where they are behind it. */
    const [openThread, setOpenThread] = useState<ThreadLabel | null>(null)
    const [threadTouched, setThreadTouched] = useState(false)
    /** Rows expanded to show details, quotes and actions; everything starts folded. */
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const toggleRow = (id: string) =>
        setExpanded((cur) => {
            const next = new Set(cur)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    useEffect(() => {
        setPage(1)
        setExpanded(new Set())
    }, [subcategoryId, evidenceState, flaggedOnly, scope.parent, scope.mailbox, scope.bucket])

    useEffect(() => {
        let cancelled = false
        setError(null)
        deepDiveApi
            .threads(scope, { subcategoryId, evidenceState, flagged: flaggedOnly, limit: PAGE, offset: (page - 1) * PAGE })
            .then((r) => {
                if (cancelled) return
                setRows(r.threads)
                setTotal(r.total)
            })
            .catch((e) => {
                if (!cancelled) setError(messageOf(e, 'Could not load the threads'))
            })
        return () => {
            cancelled = true
        }
    }, [scope, subcategoryId, evidenceState, flaggedOnly, page, version])

    const move = async (t: ThreadLabel, toId: string) => {
        if (!toId || toId === t.subcategory.id) return
        setMoving(t.conversationId)
        try {
            await deepDiveApi.relabelThread(t.conversationId, scope.parent, toId)
            onChanged()
        } catch (e) {
            setError(messageOf(e, 'Could not move the thread'))
        } finally {
            setMoving(null)
        }
    }

    const closeThread = () => {
        setOpenThread(null)
        if (threadTouched) {
            setThreadTouched(false)
            onChanged()
        }
    }
    /** The trail labels categories from this map; fall back to the parent so the outcome pill still reads well. */
    const trailMeta = useMemo(() => {
        if (categoryMeta && categoryMeta.size) return categoryMeta
        const m = new Map<string, CategoryMeta>()
        m.set(scope.parent, { key: scope.parent, label: scope.parent.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) })
        return m
    }, [categoryMeta, scope.parent])

    if (error) return <EmptyState bare tone="error" title="Could not load the threads" description={error} />
    if (!rows) return <Spinner block text="Loading threads…" />
    if (rows.length === 0) return <EmptyState bare title="No threads here" description={flaggedOnly ? 'Nothing is flagged in this group.' : evidenceState ? 'No threads in this state.' : 'No threads have this label yet.'} />

    const options = subcategories.filter((s) => s.active && !s.mergedIntoId).map((s) => ({ value: s.id, label: s.label }))

    return (
        <div className={styles.threadList}>
            <ul className={styles.threads}>
                {rows.map((t) => {
                    const who = t.thread?.counterpartyKind === 'client' ? t.thread?.clientName || t.thread?.counterpartyAddress : t.thread?.lenderName || t.thread?.counterpartyAddress
                    const quotes = t.evidence.filter((e) => e.quote)
                    const isOpen = expanded.has(t.conversationId)
                    return (
                        <li key={t.conversationId} className={`${styles.thread} ${isOpen ? styles.threadOpen : ''}`}>
                            <div
                                className={styles.threadHead}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isOpen}
                                title={isOpen ? 'Fold this thread' : 'Show what was asked, the quoted wording and actions'}
                                onClick={() => toggleRow(t.conversationId)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        toggleRow(t.conversationId)
                                    }
                                }}
                            >
                                <span className={styles.threadChevron} aria-hidden>
                                    {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                                </span>
                                <div className={styles.threadTitle}>
                                    <span className={styles.threadSubject}>{t.thread?.outcomeSubject || t.thread?.subject || '(no subject)'}</span>
                                    <div className={styles.threadMeta}>
                                        {who && <span>{who}</span>}
                                        {t.thread?.claimRef ? <span className={styles.mono}>{t.thread.claimRef}</span> : <span className={styles.muted}>no claim linked</span>}
                                        {t.thread?.counterpartyKind === 'lender' && t.thread?.clientName && <span className={styles.muted}>client {t.thread.clientName}</span>}
                                        <span className={styles.muted}>{formatDateTime(t.thread?.outcomeReceivedAt ?? t.thread?.outcomeAt ?? t.labelledAt)}</span>
                                    </div>
                                </div>
                                <div className={styles.threadPills}>
                                    {subcategoryId === null && <StatusPill size="sm" tone="info">{t.subcategory.label}</StatusPill>}
                                    <EvidencePill state={t.evidenceState} />
                                    <FlagPills flags={t.flags} />
                                    {t.confidence != null && (
                                        <span className={styles.muted} title="The model's own confidence in this label">
                                            {t.confidence}%
                                        </span>
                                    )}
                                </div>
                            </div>

                            {isOpen && (
                                <div className={styles.threadBody}>
                                    <div className={`${styles.threadCols} ${t.details.length > 0 && quotes.length > 0 ? styles.threadColsTwo : ''}`}>
                                        {t.details.length > 0 && (
                                            <section>
                                                <h5>What they asked for</h5>
                                                <ul className={styles.details}>
                                                    {t.details.map((d, i) => (
                                                        <li key={i}>{d}</li>
                                                    ))}
                                                </ul>
                                            </section>
                                        )}
                                        <section>
                                            <h5>Their words</h5>
                                            {quotes.length > 0 ? (
                                                <div className={styles.quotes}>
                                                    {quotes.map((e, i) => (
                                                        <blockquote key={i} className={`${styles.quote} ${e.verified ? styles.quoteVerified : styles.quoteUnverified}`} title={e.verified ? 'Found word for word in the email' : 'Not found in the email as written'}>
                                                            <span className={styles.quoteMark} aria-hidden>
                                                                “
                                                            </span>
                                                            {e.quote}
                                                            <span className={styles.quoteMark} aria-hidden>
                                                                ”
                                                            </span>
                                                            {e.detail && e.detail !== t.details[i] && <span className={styles.quoteDetail}> — {e.detail}</span>}
                                                            {!e.verified && <span className={styles.quoteWarn}> not found in the email</span>}
                                                        </blockquote>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className={styles.muted}>{t.labelledBy?.startsWith('human:') ? `Placed here by ${t.labelledBy.slice(6)}.` : 'No quoted evidence was returned for this thread.'}</p>
                                            )}
                                        </section>
                                    </div>

                                    {t.rationale && (
                                        <section>
                                            <h5>Why it was placed here</h5>
                                            <p className={styles.rationale}>{t.rationale}</p>
                                        </section>
                                    )}

                                    <div className={styles.threadFoot}>
                                        <Button size="sm" variant="link" onClick={() => setOpenThread(t)} title="Open this thread here, with the quoted wording highlighted">
                                            Open the thread →
                                        </Button>
                                        <label className={styles.moveTo}>
                                            <span className={styles.muted}>Move to</span>
                                            <NativeSelect small value={t.subcategory.id} options={options} disabled={moving === t.conversationId} onChange={(e) => move(t, e.target.value)} aria-label="Move this thread to another sub-category" />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </li>
                    )
                })}
            </ul>
            {total > PAGE && <Pagination page={page} pageSize={PAGE} total={total} onChange={setPage} hideSummary={false} />}

            <Modal
                open={openThread !== null}
                onClose={closeThread}
                size="xl"
                title={openThread?.thread?.outcomeSubject || openThread?.thread?.subject || 'Thread'}
                subtitle={openThread ? `${openThread.subcategory.label}${openThread.thread?.lenderName ? ` · ${openThread.thread.lenderName}` : ''}${openThread.thread?.claimRef ? ` · ${openThread.thread.claimRef}` : ''}` : undefined}
                footer={
                    <>
                        {openThread && (
                            <Button variant="neutral" onClick={() => navigate(threadHref(openThread))} title="Leave this page and open the thread in its mailbox">
                                Open in the mailbox
                            </Button>
                        )}
                        <Button onClick={closeThread}>Done</Button>
                    </>
                }
            >
                {openThread && (
                    <ConversationTrail
                        conversationId={openThread.conversationId}
                        initialMessageId={openThread.outcomeInboundId}
                        categoryMeta={trailMeta}
                        mailboxes={mailboxes}
                        onChanged={() => setThreadTouched(true)}
                        onShowAllOnClaim={(claimRef) => navigate(`/comms/mailboxes/${encodeURIComponent(openThread.thread?.mailbox ?? 'all')}?tab=received&view=threads&bucket=all&claim=${encodeURIComponent(claimRef)}`)}
                    />
                )}
            </Modal>
        </div>
    )
}
